import { useState, useEffect, useImperativeHandle, forwardRef, useRef, useCallback } from 'react'
import API from '../../services/apiService'
import { Skeleton, GMListSkeleton } from '../Skeleton/Skeleton'
import './ServerStatus.css'

// Server Status Icons
const StatusIcons = {
  online: (
    <svg viewBox="0 0 24 24" className="status-icon online">
      <circle cx="12" cy="12" r="10" fill="#22c55e"/>
      <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  offline: (
    <svg viewBox="0 0 24 24" className="status-icon offline">
      <circle cx="12" cy="12" r="10" fill="#ef4444"/>
      <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  maintenance: (
    <svg viewBox="0 0 24 24" className="status-icon maintenance">
      <circle cx="12" cy="12" r="10" fill="#f59e0b"/>
      <path d="M12 8v4M12 16h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  updating: (
    <svg viewBox="0 0 24 24" className="status-icon updating">
      <circle cx="12" cy="12" r="10" fill="#3b82f6"/>
      <path d="M12 6v6l4 2" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  degraded: (
    <svg viewBox="0 0 24 24" className="status-icon degraded">
      <circle cx="12" cy="12" r="10" fill="#f97316"/>
      <path d="M12 8v4M12 16h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  restarting: (
    <svg viewBox="0 0 24 24" className="status-icon restarting">
      <circle cx="12" cy="12" r="10" fill="#8b5cf6"/>
      <path d="M12 6a6 6 0 106 6" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M18 6v6h-6" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

// GM Crown Icon
const GMIcon = () => (
  <svg viewBox="0 0 24 24" className="gm-icon">
    <defs>
      <linearGradient id="gmGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd700"/>
        <stop offset="50%" stopColor="#c9a227"/>
        <stop offset="100%" stopColor="#8b6914"/>
      </linearGradient>
    </defs>
    <path d="M12 2L9 7l-6 1 4 4-1 6 6-3 6 3-1-6 4-4-6-1-3-5z" fill="url(#gmGold)" stroke="#8b6914" strokeWidth="1"/>
  </svg>
)

// Location Pin Icon
const LocationIcon = () => (
  <svg viewBox="0 0 24 24" className="location-icon">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#d4af37"/>
    <circle cx="12" cy="9" r="2.5" fill="#1a0d00"/>
  </svg>
)

// Time Icon
const TimeIcon = () => (
  <svg viewBox="0 0 24 24" className="time-icon">
    <circle cx="12" cy="12" r="10" fill="none" stroke="#8b6914" strokeWidth="1.5"/>
    <path d="M12 6v6l4 2" stroke="#d4af37" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
  </svg>
)

const ServerStatus = forwardRef((props, ref) => {
  const [serverStatus, setServerStatus] = useState(null)
  const [gms, setGMs] = useState([])
  const [onlineCounts, setOnlineCounts] = useState({ appClients: null, siteClients: null, characters: null })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(null)
  
  // Use refs to avoid stale closures and prevent unnecessary re-renders
  const intervalRef = useRef(null)
  const countsIntervalRef = useRef(null) // Separate fast poll for online counts
  const isLoadingRef = useRef(false) // Prevent concurrent API calls
  const serverStatusRef = useRef(null)

  // Keep serverStatusRef in sync
  useEffect(() => {
    serverStatusRef.current = serverStatus
  }, [serverStatus])

  // Calculate refresh interval based on visibility and status
  const calculateInterval = useCallback((isHidden = document.hidden) => {
    if (!isHidden) {
      const status = serverStatusRef.current?.status
      if (status === 'online') return 60000 // 1 minute for stable online
      if (status === 'offline') return 120000 // 2 minutes for offline
      return 30000 // 30 seconds for other states
    }
    return 300000 // 5 minutes when tab is hidden
  }, [])

  // Load data function - memoized to prevent recreation
  const loadData = useCallback(async () => {
    // Prevent concurrent calls
    if (isLoadingRef.current) {
      return
    }
    
    isLoadingRef.current = true
    
    try {
      // Fetch server status, recent GMs, and public online counts in parallel
      const [statusRes, gmsRes, appCountRes, siteCountRes, charCountRes] = await Promise.all([
        API.Server.getStatus(),
        API.GMs.getRecent(30), // GMs seen in the last 30 minutes
        API.Clients.getAppOnlineCount(),
        API.Clients.getSiteOnlineCount(),
        API.Characters.getOnlineCount()
      ])

      if (statusRes.success) {
        setServerStatus(statusRes.data)
      } else if (statusRes.code === 'SERVER_OFFLINE') {
        setServerStatus({
          status: 'offline',
          message: statusRes.message || null,
          estimatedRecoveryAt: statusRes.estimatedRecoveryAt || null,
          lastModifiedByAdminName: statusRes.lastModifiedByAdminName || null,
        })
      }

      if (gmsRes.success) {
        setGMs(gmsRes.data || [])
      }

      setOnlineCounts({
        appClients: appCountRes?.success ? appCountRes.data.count : null,
        siteClients: siteCountRes?.success ? siteCountRes.data.count : null,
        characters: charCountRes?.success ? charCountRes.data.count : null
      })

      setLastUpdate(new Date())
      setError(null)
    } catch (err) {
      console.error('Failed to load server status:', err)
      setError('Failed to load server status')
    } finally {
      setLoading(false)
      isLoadingRef.current = false
    }
  }, [])

  // Lightweight counts-only refresh (runs every 30s independently)
  const loadCounts = useCallback(async () => {
    try {
      const [appCountRes, siteCountRes, charCountRes] = await Promise.all([
        API.Clients.getAppOnlineCount(),
        API.Clients.getSiteOnlineCount(),
        API.Characters.getOnlineCount()
      ])
      setOnlineCounts({
        appClients: appCountRes?.success ? appCountRes.data.count : null,
        siteClients: siteCountRes?.success ? siteCountRes.data.count : null,
        characters: charCountRes?.success ? charCountRes.data.count : null
      })
    } catch {
      // silently ignore — full loadData will retry
    }
  }, [])

  // Start/restart polling with given interval
  const startPolling = useCallback((interval) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    intervalRef.current = setInterval(loadData, interval)
  }, [loadData])

  // Handle visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible - refresh immediately and restart with normal interval
        loadData()
        startPolling(calculateInterval(false))
        // Restart counts poll too
        if (countsIntervalRef.current) clearInterval(countsIntervalRef.current)
        countsIntervalRef.current = setInterval(loadCounts, 30000)
      } else {
        // Tab hidden - switch to slow polling, pause counts
        startPolling(calculateInterval(true))
        if (countsIntervalRef.current) {
          clearInterval(countsIntervalRef.current)
          countsIntervalRef.current = null
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [loadData, loadCounts, startPolling, calculateInterval])

  // Initial load and setup polling (only once on mount)
  useEffect(() => {
    loadData()
    startPolling(calculateInterval())
    // Start dedicated 30s counts poll
    countsIntervalRef.current = setInterval(loadCounts, 30000)
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      if (countsIntervalRef.current) {
        clearInterval(countsIntervalRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Intentionally empty - only run on mount

  // Update polling interval when server status changes (only if visible)
  useEffect(() => {
    if (!document.hidden && serverStatus) {
      const newInterval = calculateInterval(false)
      startPolling(newInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverStatus?.status]) // Only react to status changes

  // Expose loadData to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: loadData
  }))

  const getStatusIcon = (status) => {
    return StatusIcons[status] || StatusIcons.offline
  }

  const getStatusText = (status) => {
    const texts = {
      online: 'Online',
      offline: 'Offline',
      maintenance: 'Maintenance',
      updating: 'Updating',
      degraded: 'Degraded',
      restarting: 'Restarting'
    }
    return texts[status] || 'Unknown'
  }

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    return date.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="server-status server-status--loading">
        <div className="server-status__skeleton">
          <Skeleton width="18px" height="18px" borderRadius="50%" />
          <Skeleton width="60px" height="16px" />
        </div>
      </div>
    )
  }

  const status = serverStatus?.status || 'offline'

  return (
    <div className={`server-status ${isExpanded ? 'server-status--expanded' : ''}`}>
      {/* Compact View */}
      <div 
        className="server-status__header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="server-status__indicator">
          {getStatusIcon(status)}
          <span className={`server-status__text server-status__text--${status}`}>
            {getStatusText(status)}
          </span>
        </div>
        
        {gms.length > 0 && (
          <div className="server-status__gm-badge">
            <GMIcon />
            <span>{gms.length} GM{gms.length > 1 ? 's' : ''}</span>
          </div>
        )}

        <button className="server-status__toggle">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path 
              d={isExpanded ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} 
              stroke="currentColor" 
              strokeWidth="2" 
              fill="none" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {!isExpanded && (onlineCounts.appClients !== null || onlineCounts.siteClients !== null || onlineCounts.characters !== null) && (
        <div className="server-status__compact-counts">
          {onlineCounts.appClients !== null && (
            <div className="server-status__compact-count">
              <span className="server-status__compact-label">App</span>
              <span className="server-status__compact-value server-status__compact-value--app">{onlineCounts.appClients}</span>
            </div>
          )}
          {onlineCounts.siteClients !== null && (
            <div className="server-status__compact-count">
              <span className="server-status__compact-label">Web</span>
              <span className="server-status__compact-value server-status__compact-value--site">{onlineCounts.siteClients}</span>
            </div>
          )}
          {onlineCounts.characters !== null && (
            <div className="server-status__compact-count">
              <span className="server-status__compact-label">Chars</span>
              <span className="server-status__compact-value">{onlineCounts.characters}</span>
            </div>
          )}
        </div>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div className="server-status__details">
          {/* Online Counts */}
          {(onlineCounts.appClients !== null || onlineCounts.siteClients !== null || onlineCounts.characters !== null) && (
            <div className="server-status__online-counts">
              {onlineCounts.appClients !== null && (
                <div className="server-status__online-count server-status__online-count--app">
                  <svg viewBox="0 0 24 24" className="online-count-icon" width="14" height="14">
                    <rect x="4" y="4" width="16" height="16" rx="3" fill="none" stroke="#22c55e" strokeWidth="1.5"/>
                    <path d="M8 12l3 3 5-5" fill="none" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="online-count-label">App Users</span>
                  <span className="online-count-value">{onlineCounts.appClients}</span>
                </div>
              )}
              {onlineCounts.siteClients !== null && (
                <div className="server-status__online-count server-status__online-count--site">
                  <svg viewBox="0 0 24 24" className="online-count-icon" width="14" height="14">
                    <circle cx="12" cy="12" r="9" fill="none" stroke="#3b82f6" strokeWidth="1.5"/>
                    <ellipse cx="12" cy="12" rx="4" ry="9" fill="none" stroke="#3b82f6" strokeWidth="1"/>
                    <line x1="3" y1="12" x2="21" y2="12" stroke="#3b82f6" strokeWidth="1"/>
                    <path d="M4 8 Q12 6 20 8" fill="none" stroke="#3b82f6" strokeWidth="0.75"/>
                    <path d="M4 16 Q12 18 20 16" fill="none" stroke="#3b82f6" strokeWidth="0.75"/>
                  </svg>
                  <span className="online-count-label">Web Users</span>
                  <span className="online-count-value">{onlineCounts.siteClients}</span>
                </div>
              )}
              {onlineCounts.characters !== null && (
                <div className="server-status__online-count">
                  <svg viewBox="0 0 24 24" className="online-count-icon" width="14" height="14">
                    <path d="M12 2a5 5 0 015 5c0 2-1 3.5-2.5 4.5L16 21H8l1.5-9.5C8 10.5 7 9 7 7a5 5 0 015-5z" fill="none" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="online-count-label">Characters</span>
                  <span className="online-count-value">{onlineCounts.characters}</span>
                </div>
              )}
            </div>
          )}

          {/* Server Message */}
          {serverStatus?.message && (
            <div className="server-status__message">
              <span className="server-status__message-icon">📢</span>
              {serverStatus.message}
            </div>
          )}

          {/* Last Modified By Admin */}
          {serverStatus?.lastModifiedByAdminName && (
            <div className="server-status__modified-by">
              <span className="server-status__admin-icon">👤</span>
              <span>Modified by: <strong>{serverStatus.lastModifiedByAdminName}</strong></span>
            </div>
          )}

          {/* Estimated Recovery */}
          {serverStatus?.estimatedRecoveryAt && status !== 'online' && (
            <div className="server-status__recovery">
              <TimeIcon />
              <span>Estimated recovery: {new Date(serverStatus.estimatedRecoveryAt).toLocaleString()}</span>
            </div>
          )}

          {/* Active GMs */}
          {gms.length > 0 && (
            <div className="server-status__gms">
              <h4 className="server-status__gms-title">
                <GMIcon /> Active Game Masters
              </h4>
              <div className="server-status__gms-list">
                {gms.map((gm, index) => (
                  <div key={gm.id || index} className="server-status__gm-item">
                    <span className="server-status__gm-name">{gm.name}</span>
                    {gm.location && (
                      <span className="server-status__gm-location">
                        <LocationIcon />
                        {gm.location}
                      </span>
                    )}
                    <span className="server-status__gm-time">
                      {formatTimeAgo(gm.lastSeenAt || gm.updatedAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {gms.length === 0 && (
            <div className="server-status__no-gms">
              <GMIcon />
              <span>No Game Masters spotted recently</span>
            </div>
          )}

          {/* Refresh Button */}
          <div className="server-status__footer">
            {lastUpdate && (
              <span className="server-status__last-update">
                Last updated: {formatTimeAgo(lastUpdate.toISOString())}
              </span>
            )}
            <button className="server-status__refresh" onClick={loadData}>
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path d="M21 12a9 9 0 11-9-9" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
                <path d="M21 3v6h-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

export default ServerStatus
